import React from 'react';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import DropdownAutocomplete from '../../../components/FormFields/DropdownAutocomplete';
import type { PickerOption } from '../linkBuilderOptions';

interface Props {
  id: string;
  label: string;
  paramKey: string;
  options: PickerOption[];
  value: number | null;
  loading: boolean;
  error: boolean;
  hint?: string;
  warning?: string;
  onChange: (id: number | null) => void;
  onRemove: () => void;
  /** Under the field, after the hints: a control that changes what the field lists. */
  extra?: React.ReactNode;
}

const EntityParamRow: React.FC<Props> = ({
  id,
  label,
  paramKey,
  options,
  value,
  loading,
  error,
  hint,
  warning,
  onChange,
  onRemove,
  extra,
}) => (
  <Box>
    <Typography component='label' htmlFor={id} variant='subtitle2' sx={{ display: 'block', mb: 0.5 }}>
      {label}{' '}
      <Typography component='span' variant='caption' color='text.secondary' sx={{ fontFamily: 'monospace' }}>
        {paramKey}
      </Typography>
    </Typography>
    <Stack direction='row' spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1 }}>
        <DropdownAutocomplete
          id={id}
          options={options}
          value={value}
          valueField='id'
          textField='label'
          busy={loading}
          error={error}
          placeholder={`Search ${label.toLowerCase()} by id or name…`}
          clearable
          onChange={(selected: PickerOption | null) => onChange(selected?.id ?? null)}
        />
      </Box>
      {/* Named by param key, not label: `getByLabelText(/Landing page/)` must match only the picker. */}
      <IconButton aria-label={`Remove ${paramKey}`} onClick={onRemove}>
        <DeleteOutlined />
      </IconButton>
    </Stack>
    {error && <FormHelperText error>Couldn&apos;t load {label.toLowerCase()}s.</FormHelperText>}
    {warning && <FormHelperText sx={{ color: 'warning.main' }}>{warning}</FormHelperText>}
    {hint && <FormHelperText>{hint}</FormHelperText>}
    {extra}
  </Box>
);

export default EntityParamRow;
